import React, { useState } from "react";
import axios from "axios";
import { X, Pencil, Calendar, Archive, Trash2, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INPUT = {
  width: "100%",
  background: "rgba(255,255,255,0.6)",
  border: "1px solid rgba(26,77,46,0.18)",
  borderRadius: "0.5rem",
  padding: "0.6rem 0.875rem",
  color: "#0F172A",
  fontSize: "0.9rem",
  fontFamily: "'Manrope', sans-serif",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const LABEL = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  fontFamily: "'Manrope', sans-serif",
  color: "#374151",
  fontSize: "0.78rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

function StatusMsg({ msg }) {
  if (!msg) return null;
  const ok = msg.type === "success";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "0.4rem",
      marginTop: "0.5rem",
      color: ok ? "#3d6b1f" : "#dc2626",
      background: ok ? "rgba(61,107,31,0.08)" : "rgba(220,38,38,0.08)",
      border: `1px solid ${ok ? "rgba(61,107,31,0.2)" : "rgba(220,38,38,0.2)"}`,
      borderRadius: "0.4rem", padding: "0.4rem 0.75rem",
      fontFamily: "'Manrope', sans-serif", fontSize: "0.8rem",
    }}>
      {ok ? <Check size={13} /> : <AlertCircle size={13} />}
      {msg.text}
    </div>
  );
}

export default function ProjectSettingsModal({ project, token, onClose, onUpdated, onDeleted }) {
  const [title, setTitle] = useState(project.title);
  const [dueDate, setDueDate] = useState(
    project.target_completion ? project.target_completion.substring(0, 10) : ""
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    if (!title.trim()) { setMsg({ type: "error", text: "Project name cannot be empty." }); return; }
    setSaving(true);
    setMsg(null);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/projects/${project.id}`,
        { title: title.trim(), target_completion: dueDate || null },
        { headers: authHeaders }
      );
      setMsg({ type: "success", text: "Project updated." });
      onUpdated?.({ ...project, title: title.trim(), target_completion: dueDate || null });
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/projects/${project.id}`,
        { status: "archived" },
        { headers: authHeaders }
      );
      onDeleted?.(project.id);
      onClose();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Failed to archive." });
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${project.id}`, { headers: authHeaders });
      onDeleted?.(project.id);
      onClose();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Failed to delete." });
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="project-settings-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 70,
        background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="project-settings-modal"
        style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.5)",
          borderRadius: "1rem",
          boxShadow: "0 12px 48px rgba(26,77,46,0.16)",
          width: "100%", maxWidth: "440px",
          padding: "1.5rem",
          position: "relative",
          animation: "fadeInDown 0.2s ease",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
          <span style={{ fontSize: "1.5rem" }}>{project.emoji}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1rem", fontWeight: 700, color: "#1A4D2E", margin: 0 }}>
              Project Settings
            </h2>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", color: "#6b7280", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {project.title}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(26,77,46,0.06)", border: "1px solid rgba(26,77,46,0.15)",
              borderRadius: "50%", width: "28px", height: "28px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#6b7280", flexShrink: 0,
            }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Name */}
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={LABEL}><Pencil size={11} /> Project Name</label>
          <input
            data-testid="project-settings-name-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={INPUT}
            onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
          />
        </div>

        {/* Due date */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={LABEL}><Calendar size={11} /> Due Date</label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              data-testid="project-settings-due-date-input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              style={{ ...INPUT, width: "auto", minWidth: "180px" }}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
            />
            {dueDate && (
              <button
                onClick={() => setDueDate("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "0.75rem", fontFamily: "'Manrope', sans-serif" }}
              >
                clear
              </button>
            )}
          </div>
        </div>

        {/* Save row */}
        <button
          data-testid="project-settings-save-btn"
          onClick={handleSave}
          disabled={saving}
          style={{
            width: "100%", padding: "0.6rem",
            background: saving ? "rgba(61,107,31,0.45)" : "#3d6b1f",
            border: "none", borderRadius: "0.5rem",
            color: "#fff", fontFamily: "'Manrope', sans-serif",
            fontSize: "0.875rem", fontWeight: 600,
            cursor: saving ? "not-allowed" : "pointer",
            transition: "background 0.2s", marginBottom: "0.5rem",
          }}
          onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "#4a7f25"; }}
          onMouseLeave={(e) => { if (!saving) e.currentTarget.style.background = "#3d6b1f"; }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>

        <StatusMsg msg={msg} />

        {/* Divider */}
        <div style={{ height: "1px", background: "rgba(26,77,46,0.1)", margin: "1.25rem 0" }} />

        {/* Danger zone */}
        <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 0.75rem" }}>
          Danger Zone
        </p>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          {/* Archive */}
          <button
            data-testid="project-settings-archive-btn"
            onClick={handleArchive}
            disabled={saving}
            style={{
              flex: 1, padding: "0.55rem 0.75rem",
              background: "rgba(217,119,6,0.08)", border: "1.5px solid rgba(217,119,6,0.3)",
              borderRadius: "0.5rem", color: "#d97706",
              fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "0.35rem",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "rgba(217,119,6,0.15)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(217,119,6,0.08)"; }}
          >
            <Archive size={14} /> Archive
          </button>

          {/* Delete */}
          {!confirmDelete ? (
            <button
              data-testid="project-settings-delete-btn"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              style={{
                flex: 1, padding: "0.55rem 0.75rem",
                background: "rgba(220,38,38,0.08)", border: "1.5px solid rgba(220,38,38,0.3)",
                borderRadius: "0.5rem", color: "#dc2626",
                fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "0.35rem",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "rgba(220,38,38,0.15)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.08)"; }}
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : (
            <div style={{ flex: 1, display: "flex", gap: "0.35rem" }}>
              <button
                data-testid="project-settings-confirm-delete-btn"
                onClick={handleDelete}
                disabled={saving}
                style={{
                  flex: 1, padding: "0.55rem 0.5rem",
                  background: "#dc2626", border: "none",
                  borderRadius: "0.5rem", color: "#fff",
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", fontWeight: 700,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                Yes, delete all
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                style={{
                  padding: "0.55rem 0.5rem",
                  background: "rgba(26,77,46,0.06)", border: "1px solid rgba(26,77,46,0.15)",
                  borderRadius: "0.5rem", color: "#374151",
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        {confirmDelete && (
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#dc2626", fontSize: "0.75rem", marginTop: "0.5rem", textAlign: "center" }}>
            This will permanently delete the project and all its tasks.
          </p>
        )}
      </div>
    </div>
  );
}
